import React from 'react'

import { Button, ConfirmationDialog, Icon, useDialogState } from '../../components'
import { COMMON_FOOTER_BUTTON_PROPS } from './constants'
import { PreventClosingUnsavedChanges } from './prevent-closing-unsaved-changes'

interface SettingsActionFooterProps {
  onActionButtonClick?: (e: React.FormEvent<HTMLFormElement>) => Promise<void>
  actionButtonFormId?: string
  isActionPending?: boolean
  tabButtonTestId?: string

  onSettingsReset: () => void
  resetSettingsButtonLabel: string

  settingsTabButtonIconName: string
  settingsTabButtonLabel: React.ReactNode
  onSettingsTabButtonClick: () => void

  resetConfirmationTitle: string
  resetConfirmationText: string
}

export function SettingsActionFooter({
  actionButtonFormId,
  tabButtonTestId,
  resetSettingsButtonLabel,
  settingsTabButtonIconName,
  settingsTabButtonLabel,
  resetConfirmationTitle,
  resetConfirmationText,
  isActionPending = false,
  onSettingsReset,
  onActionButtonClick,
  onSettingsTabButtonClick,
}: SettingsActionFooterProps) {
  const confirmationDialogState = useDialogState()

  return (
    <div className="flex w-full items-center justify-between">
      <div className="ml-1 flex space-x-2">
        <Button
          type={actionButtonFormId ? 'submit' : undefined}
          theme="blue"
          form={actionButtonFormId}
          onClick={onActionButtonClick}
          disabled={isActionPending}
          data-testid="chart-settings-save-btn"
        >
          {isActionPending ? 'Saving...' : 'Save'}
        </Button>
        <Button
          onClick={() => {
            confirmationDialogState.show()
          }}
          disabled={isActionPending}
          data-testid="chart-settings-reset-defaults-btn"
        >
          {resetSettingsButtonLabel}
        </Button>
      </div>

      <PreventClosingUnsavedChanges onHide={onSettingsTabButtonClick}>
        {({ checkForUnsavedChangesAndClose }) => (
          <Button
            {...COMMON_FOOTER_BUTTON_PROPS}
            onClick={checkForUnsavedChangesAndClose}
            leftContent={<Icon name={settingsTabButtonIconName} width={20} className="text-gray-500" />}
            data-testid={tabButtonTestId}
          >
            {settingsTabButtonLabel}
          </Button>
        )}
      </PreventClosingUnsavedChanges>

      <ConfirmationDialog
        state={confirmationDialogState}
        aria-label={resetConfirmationTitle}
        title={resetConfirmationTitle}
        actions={
          <>
            <Button data-testid="confirmation-dialog-cancel" onClick={() => confirmationDialogState.hide()}>
              Cancel
            </Button>
            <Button
              data-testid="confirmation-dialog-delete"
              onClick={() => {
                onSettingsReset()
                confirmationDialogState.hide()
              }}
              theme="red"
            >
              Confirm
            </Button>
          </>
        }
      >
        {resetConfirmationText}
      </ConfirmationDialog>
    </div>
  )
}
